/**
 * The health and armour indicator.
 *
 * Above the hotbar rather than in the corner with the debug text, because it
 * is the one thing on screen a player looks at without deciding to: the eye is
 * already at the hotbar choosing what to hold, and сила has to be in the same
 * glance.
 *
 * Помідори for сила and notches for броня, rather than a smooth bar - see
 * core/entity/gauge.ts for why a row you can count beats a length you have to
 * judge. This file only draws what that says.
 *
 * The elements are built once and only their classes and widths change, so a
 * frame that updates the indicator allocates nothing.
 */

import { gaugeSegments } from "../core/entity/gauge.js";
import { atlasDataUrl } from "../render/atlas.js";
import { ATLAS_TILES } from "../core/block/tiles.js";
import { ui, type Lang } from "../core/text/lang.js";

/** Which tile the помідор is painted on. */
const TOMATO_TILE = 102;
/** Edge of one помідор on screen, in CSS pixels. */
const TOMATO_PX = 17;

/**
 * Помідори on the сила row. Twenty hit points across ten of them, so one
 * помідор is two points and half a помідор is one.
 *
 * Not hearts. Hearts are the most recognisable thing in the game this one is
 * deliberately not copying, and a помідор off your own грядка says more about
 * where сила comes from than a heart does.
 */
const HEALTH_NOTCHES = 10;
/**
 * Notches on the броня row.
 *
 * Eight, which is the full iron set. Better metal will one day go past it and
 * simply fill every notch - a bar that grew a notch per tier would move the
 * numbers under the player's eye every time they forged something.
 */
const ARMOUR_NOTCHES = 8;

export class VitalsUI {
  /** Set by main.ts. */
  lang: Lang = "uk";
  private readonly root: HTMLDivElement;
  private readonly healthRow: HTMLDivElement;
  private readonly armourRow: HTMLDivElement;
  private readonly healthNotches: HTMLDivElement[] = [];
  private readonly armourNotches: HTMLDivElement[] = [];
  private readonly healthCount: HTMLSpanElement;
  private readonly armourCount: HTMLSpanElement;

  /** What was last drawn, so an unchanged frame touches no DOM. */
  private lastHp = NaN;
  private lastMax = NaN;
  private lastPoints = NaN;

  constructor(host: HTMLElement) {
    this.root = document.createElement("div");
    this.root.id = "vitals";

    const build = (
      label: string, kind: string, count: number, notches: HTMLDivElement[],
    ): [HTMLDivElement, HTMLSpanElement] => {
      const row = document.createElement("div");
      row.className = `vital-row vital-${kind}`;

      const name = document.createElement("span");
      name.className = "vital-label";
      name.textContent = label;

      const bar = document.createElement("div");
      bar.className = "vital-bar";
      for (let i = 0; i < count; i++) {
        const notch = document.createElement("div");
        if (kind === "health") {
          // A помідор is drawn twice, as two siblings in an empty box: a
          // dark one that is always there, and a ripe one clipped over it.
          // That is what makes a half помідор read as half a помідор rather
          // than as a smaller one.
          //
          // Siblings rather than one inside the other, because a CSS filter
          // applies to the whole subtree: a ripe помідор nested inside a
          // greyed-out one would come out grey however it filtered itself.
          notch.className = "tomato";
          const dim = document.createElement("div");
          dim.className = "tomato-dim";
          const ripe = document.createElement("div");
          ripe.className = "tomato-ripe";
          notch.append(dim, ripe);
        } else {
          notch.className = "vital-notch";
        }
        notches.push(notch);
        bar.appendChild(notch);
      }

      const readout = document.createElement("span");
      readout.className = "vital-count";

      row.append(name, bar, readout);
      return [row, readout];
    };

    [this.healthRow, this.healthCount] =
      build(ui("hud-health", this.lang), "health", HEALTH_NOTCHES, this.healthNotches);
    [this.armourRow, this.armourCount] =
      build(ui("hud-armour", this.lang), "armour", ARMOUR_NOTCHES, this.armourNotches);

    // Both помідори in a slot come off the same atlas at the same spot; the
    // dark one is the ripe one with the colour taken out, which is done in
    // CSS rather than by painting a second tile.
    const sheet = TOMATO_PX * ATLAS_TILES;
    const col = TOMATO_TILE % ATLAS_TILES;
    const row = Math.floor(TOMATO_TILE / ATLAS_TILES);
    this.root.style.setProperty("--tomato-sheet", `${sheet}px ${sheet}px`);
    this.root.style.setProperty("--tomato-at",
      `${-col * TOMATO_PX}px ${-row * TOMATO_PX}px`);
    this.root.style.setProperty("--tomato-url", `url(${atlasDataUrl()})`);
    this.root.style.setProperty("--tomato-px", `${TOMATO_PX}px`);

    this.root.append(this.healthRow, this.armourRow);
    host.appendChild(this.root);
  }

  /**
   * Redraw, if anything moved.
   *
   * Called every frame, so the comparison is the point: without it this would
   * write sixty times a second to say the same thing.
   */
  update(hp: number, max: number, armourPoints: number, relief: number): void {
    if (hp === this.lastHp && max === this.lastMax && armourPoints === this.lastPoints) {
      return;
    }
    this.lastHp = hp;
    this.lastMax = max;
    this.lastPoints = armourPoints;

    const health = gaugeSegments(hp, max, HEALTH_NOTCHES);
    for (let i = 0; i < this.healthNotches.length; i++) {
      this.paint(this.healthNotches[i]!, health[i] ?? 0);
    }
    // Rounded up: a body on half a point of сила is not a dead one, and the
    // number saying 0 while the player is still walking about reads as broken.
    this.healthCount.textContent = `${Math.ceil(Math.max(0, hp))}/${max}`;
    // The whole row goes red when the next blow could be the last one.
    this.healthRow.classList.toggle("low", max > 0 && hp / max <= 0.3);

    const armour = gaugeSegments(armourPoints, ARMOUR_NOTCHES, ARMOUR_NOTCHES);
    for (let i = 0; i < this.armourNotches.length; i++) {
      this.paint(this.armourNotches[i]!, armour[i] ?? 0);
    }
    this.armourCount.textContent = `−${relief}%`;
    // Hidden entirely when there is none: an empty броня row on a player who
    // has never smelted iron is a row about something that does not exist yet.
    this.armourRow.hidden = armourPoints <= 0;
  }

  private paint(notch: HTMLDivElement, fill: number): void {
    notch.classList.toggle("full", fill >= 1);
    notch.classList.toggle("part", fill > 0 && fill < 1);
    // Drawn from the left, which is the direction the row empties from.
    notch.style.setProperty("--fill", `${Math.round(fill * 100)}%`);
  }
}
